import { ICreateSupplier } from "@/@types/supplier";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useCreateSupplierMutation } from "@/services/supplier";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "components/ui/form";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { toast } from "components/ui/use-toast";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { businessUnit, type } from "./utils";
import { MESSAGES } from "./utils/messages";
import { FormSchema } from "./validation-forms";

import { Load } from "components/load";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "components/ui/alert-dialog";

export function CreateSupplier() {
  const [createSupplier, { isLoading }] = useCreateSupplierMutation();
  const { handleModal } = useDialogModal();
  const navigate = useNavigate();

  const form = useForm<ICreateSupplier>({
    resolver: zodResolver(FormSchema),
  });

  function formatCNPJ(value: string) {
    return value
      .replace(/\D/g, "")
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 18);
  }

  async function onSubmit(data: ICreateSupplier) {
    try {
      await createSupplier(data).unwrap();
      handleModal({ isOpen: false });
      navigate("/fiscal/fornecedores");
      toast(MESSAGES.CREATE.SUCCESS);
      form.reset();
    } catch (e) {
      toast(MESSAGES.CREATE.ERROR);
    }
  }

  if (isLoading) return <Load />;

  return (
    <PageLayout>
      <PageTitle
        title="Cadastro de Fornecedor"
        description="Preencha os campos abaixo para adicionar um novo fornecedor."
      />
      <ContentContainer>
        <div className="flex flex-col items-center w-full">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-3xl">
        <Form {...form}>
          <form className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <FormField
                control={form.control}
                name="supplier_name"
                render={({ field }) => (
                  <FormItem>
                    <Label className="text-gray-700 font-medium">
                      Fornecedor
                    </Label>
                    <Input
                      {...field}
                      hasError={!!form.formState.errors.supplier_name}
                      name="supplier_name"
                      placeholder="Digite o nome do fornecedor"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Copie exatamente como está na Oracle. Use{" "}
                      <kbd className="px-1 bg-gray-100 rounded font-mono text-sm">
                        Ctrl + A
                      </kbd>{" "}
                      na Oracle para selecionar e colar aqui.
                    </p>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="supplier_number"
              render={({ field }) => (
                <FormItem>
                  <Label className="text-gray-700 font-medium">
                    Número do Fornecedor
                  </Label>
                  <Input
                    {...field}
                    hasError={!!form.formState.errors.supplier_number}
                    name="supplier_number"
                    placeholder="Digite o número do fornecedor"
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cnpj"
              render={({ field }) => (
                <FormItem>
                  <Label className="text-gray-700 font-medium">CNPJ</Label>
                  <Input
                    {...field}
                    value={field.value}
                    onChange={(e) => field.onChange(formatCNPJ(e.target.value))}
                    hasError={!!form.formState.errors.cnpj}
                    name="cnpj"
                    placeholder="Digite o CNPJ do fornecedor"
                    maxLength={18}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <Label className="text-gray-700 font-medium">Tipo</Label>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger hasError={!!form.formState.errors.type}>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {type.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="business_unit"
              render={({ field }) => (
                <FormItem>
                  <Label className="text-gray-700 font-medium">
                    Unidade de Negócio
                  </Label>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger
                        hasError={!!form.formState.errors.business_unit}
                      >
                        <SelectValue placeholder="Selecione a unidade de negócio" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {businessUnit.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem>
                  <Label className="text-gray-700 font-medium">Status</Label>
                  <Select
                    onValueChange={(value) => field.onChange(value === "true")}
                    value={field.value !== undefined ? String(field.value) : ""}
                  >
                    <FormControl>
                      <SelectTrigger
                        hasError={!!form.formState.errors.is_active}
                      >
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="true">Ativo</SelectItem>
                      <SelectItem value="false">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="col-span-2 flex justify-end gap-4 mt-4">
              <Button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-200 transition"
                onClick={() => navigate(-1)}
              >
                Voltar
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="default" disabled={isLoading}>
                    Salvar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-md bg-white text-gray-800 border border-gray-300 shadow-lg">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-lg font-semibold">
                      Confirme antes de prosseguir
                    </AlertDialogTitle>
                  </AlertDialogHeader>

                  <div className="text-sm leading-relaxed mt-2">
                    Verifique se o <strong>nome do fornecedor</strong> está
                    exatamente igual ao registrado na Oracle.
                    <br />
                    <br />
                    Se necessário, use{" "}
                    <kbd className="bg-gray-100 px-1 rounded text-xs font-mono">
                      Ctrl + A
                    </kbd>{" "}
                    na Oracle e cole no campo <strong>Fornecedor</strong> antes
                    de salvar.
                  </div>

                  <AlertDialogFooter className="mt-6">
                    <AlertDialogCancel className="bg-gray-100 hover:bg-gray-200 text-gray-700">
                      Cancelar
                    </AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-[#F5CB2B] hover:bg-[#F5CB2B] "
                      onClick={form.handleSubmit(onSubmit)}
                    >
                      Sim, criar fornecedor
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </form>
        </Form>
      </div>
        </div>
      </ContentContainer>
    </PageLayout>
  );
}
